import {
	onBeforeInputNumeric,
	onDropNumeric,
	onPasteNumeric,
	validateNumericInput,
} from '@/components/blocks/AddressForm/utils';
import { useZipCodeInput } from '@/components/content/Header/parts/DeliveryLocator/hooks/useZipCodeInput';
import {
	geoLocationTriggerSX,
	zipCodeFormBtnSX,
	zipCodeFormContainerSX,
	zipCodeFormInputSX,
} from '@/components/content/Header/parts/DeliveryLocator/styles/ZipCodeInput/zipCodeInputStyles';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import {
	Box,
	Button,
	CircularProgress,
	InputAdornment,
	Link,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { Dispatch, SetStateAction, useContext } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

type Props = {
	zipCodeInputValue: string;
	setOpenGeolocalizationRequestView: Dispatch<SetStateAction<boolean>>;
};

export const ZipCodeInput = ({ zipCodeInputValue, setOpenGeolocalizationRequestView }: Props) => {
	const localization = useLocalization('DeliveryLocator');
	const theme = useTheme();

	const {
		focused,
		handleOnBlur,
		handleSubmit,
		handleZipCodeInputChange,
		loading,
		setFocused,
		zipCodeError,
	} = useContext(ContentContext) as ReturnType<typeof useZipCodeInput>;

	return (
		<Stack gap={1.25}>
			<Link
				id="geolocateLocationPanel"
				sx={geoLocationTriggerSX}
				onClick={() => setOpenGeolocalizationRequestView(true)}
			>
				<DynamicIcon name={MATERIAL_SYMBOL_ICONS.myLocation} size="24px" />
				<Typography variant="bodyBaseline">{localization.useCurrentLocation.t()}</Typography>
			</Link>
			<Box component={'form'} onSubmit={handleSubmit}>
				<Box sx={zipCodeFormContainerSX}>
					<TextField
						id="zipCodeInput"
						name="zipCode"
						label={focused ? localization.Labels.zipCode.t() : ''}
						placeholder={localization.Msgs.zipCodeInputPlaceHolder.t()}
						value={zipCodeInputValue}
						fullWidth
						onFocus={() => setFocused(true)}
						onBlur={handleOnBlur}
						onChange={handleZipCodeInputChange}
						autoComplete="postal-code"
						inputProps={{
							maxLength: 5,
						}}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<DynamicIcon
										name={MATERIAL_SYMBOL_ICONS.search}
										color={theme.palette.primary.main}
									/>
								</InputAdornment>
							),
						}}
						error={zipCodeError}
						helperText={
							zipCodeError ? (
								<Box className="delivery-location__alert-message">
									{localization.Msgs.invalidZipCode.t()}
								</Box>
							) : (
								''
							)
						}
						onKeyDown={validateNumericInput}
						onBeforeInput={onBeforeInputNumeric}
						onPaste={onPasteNumeric}
						onDrop={onDropNumeric}
						inputMode="numeric"
						sx={zipCodeFormInputSX}
					/>
					<Button
						id="zipCodeUpdateBtn"
						type="submit"
						variant="outlined"
						fullWidth
						sx={zipCodeFormBtnSX}
						startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
					>
						{loading ? null : (
							<Typography variant="bodyBaseline">{localization.updateLocation.t()}</Typography>
						)}
					</Button>
				</Box>
			</Box>
		</Stack>
	);
};
